import { useState, useEffect, useRef } from 'react'
import { useImageCache } from '../hooks/useImageCache'

interface NewsImageProps {
  imageKey: string | null
  className?: string
  alt?: string
  fallback?: string
}

const NewsImage = ({ imageKey, className = '', alt = 'News image', fallback }: NewsImageProps) => {
  const { getCachedUrl, loadImageUrl } = useImageCache()
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const currentKeyRef = useRef<string | null>(null)
  const functionsRef = useRef({ getCachedUrl, loadImageUrl })
  
  // Actualizar la referencia cuando las funciones cambien
  functionsRef.current = { getCachedUrl, loadImageUrl }

  useEffect(() => {
    // Si no hay imageKey, usar fallback
    if (!imageKey) {
      if (currentKeyRef.current !== null) {
        setImageUrl(fallback || null)
        setLoading(false)
        setError(false)
        currentKeyRef.current = null
      }
      return
    }

    // Si la key no ha cambiado, no hacer nada (evitar recargas innecesarias)
    if (currentKeyRef.current === imageKey) {
      return
    }

    // Verificar caché primero
    const cachedUrl = functionsRef.current.getCachedUrl(imageKey)
    if (cachedUrl) {
      setImageUrl(cachedUrl)
      setLoading(false)
      setError(false)
      currentKeyRef.current = imageKey
      return
    }

    // Si no está en caché, cargarla
    currentKeyRef.current = imageKey
    setLoading(true)
    setError(false)
    
    functionsRef.current.loadImageUrl(imageKey).then((url) => {
      // Verificar que la key no haya cambiado mientras se cargaba
      if (currentKeyRef.current === imageKey) {
        if (url) {
          setImageUrl(url)
        } else {
          setError(true)
          setImageUrl(fallback || null)
        }
        setLoading(false)
      }
    }).catch((error) => {
      console.error('Error loading news image signed URL:', error)
      if (currentKeyRef.current === imageKey) {
        setError(true)
        setImageUrl(fallback || null)
        setLoading(false)
      }
    })
  }, [imageKey, fallback])

  if (loading) {
    return (
      <div className={`bg-gradient-to-br from-gray-100 to-gray-200 animate-pulse ${className}`}></div>
    )
  }

  if (!imageUrl || error) {
    return (
      <div className={`bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center ${className}`}>
        <span className="text-primary-600 text-sm font-medium">Sin imagen</span>
      </div>
    )
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={className}
      onError={() => {
        setError(true)
        setImageUrl(fallback || null)
      }}
    />
  )
}

export default NewsImage

